// client/src/components/pages/LexiStar/Maintenance/MaintenanceTimeline.tsx (ST-00249)
//
// One line per truck over the chosen window, drawn from the server's time model (GET
// /api/lexistar/time-model?segments=true): each stretch in its category's colour — Breakdown
// (rose), Planned maintenance (purple), Delay (orange), the shared asset status tokens
// (map/assetStatus via CATEGORY_META), so the legend and the bars match the control room, the
// Status Board and the maps — and Available as the empty track; what
// is booked after now is striped; a line marks now. A stretch is a button: it opens the event
// that decided it (the strongest of the events under it). Beside each truck, its available share
// of the window. Colour is never the only signal: every stretch has its words (aria-label and
// title), the legend has icons.

import type { CSSProperties } from 'react';
import type { AssetTimeModel, TimeEvent, TimeSegment } from '../../../../types/lexistar-maintenance';
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  PROJECTED_STYLE,
  barsOf,
  fmtDayTime,
  fmtDuration,
  nowLeft,
  pctText,
  ticksOf,
} from './maintenanceLogic';

export interface MaintenanceTimelineProps {
  assets: AssetTimeModel[];
  fromMs: number;
  toMs: number;
  now: number;
  eventsById: Map<string, TimeEvent>;
  onOpenEvent: (id: string) => void;
  /** Phone: the truck's name above its track. */
  phone?: boolean;
}

export function Legend({ compact = false }: { compact?: boolean }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-700" aria-label="Legend">
      {CATEGORY_ORDER.map((c) => {
        const m = CATEGORY_META[c];
        return (
          <li key={c} className="inline-flex items-center gap-1.5">
            <span
              className={`inline-block w-4 h-2.5 rounded-sm ${m.bar} ${c === 'available' ? 'ring-1 ring-inset ring-slate-300' : ''}`}
              aria-hidden="true"
              data-swatch={c}
            />
            <m.Icon className={`${m.text}`} aria-hidden="true" />
            {compact ? m.short : m.label}
          </li>
        );
      })}
      <li className="inline-flex items-center gap-1.5">
        <span
          className={`inline-block w-4 h-2.5 rounded-sm ${CATEGORY_META.planned_maintenance.bar}`}
          style={PROJECTED_STYLE as CSSProperties}
          aria-hidden="true"
          data-swatch="booked"
        />
        Booked ahead
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span className="inline-block w-0.5 h-3 bg-primary-600" aria-hidden="true" />
        Now
      </li>
    </ul>
  );
}

function barLabel(s: TimeSegment, e: TimeEvent | undefined): string {
  const what = e ? `${e.label} (${CATEGORY_META[s.category].label})` : CATEGORY_META[s.category].label;
  const span = `${fmtDayTime(s.start)} to ${fmtDayTime(s.end)}, ${fmtDuration(Date.parse(s.end) - Date.parse(s.start))}`;
  const more = s.event_ids.length > 1 ? `, ${s.event_ids.length - 1} more event${s.event_ids.length > 2 ? 's' : ''} under it` : '';
  return `${what}: ${span}${s.projected ? ', booked' : ''}${more}`;
}

export default function MaintenanceTimeline({ assets, fromMs, toMs, now, eventsById, onOpenEvent, phone = false }: MaintenanceTimelineProps) {
  const ticks = ticksOf(fromMs, toMs, phone ? { short: true, maxLabels: 8 } : {});
  const nowAt = nowLeft(fromMs, toMs, now);
  const label = phone ? '' : 'sm:grid-cols-[9.5rem_1fr]';
  return (
    <div className="space-y-1" data-testid="maintenance-timeline">
      {/* Day ruler */}
      <div className={`grid grid-cols-1 ${label} gap-3`} aria-hidden="true">
        {!phone && <span className="hidden sm:block" />}
        <div className="relative h-5 text-[11px] text-slate-500">
          {ticks
            .filter((t) => t.major && t.label)
            .map((t) => (
              <span key={`l${t.left}`} className="absolute top-0 whitespace-nowrap pl-1" style={{ left: `${t.left}%` }}>
                {t.label}
              </span>
            ))}
        </div>
      </div>
      <ul className="space-y-1.5" aria-label="Trucks over the window">
        {assets.map((a) => {
          const bars = barsOf(a.segments || [], fromMs, toMs);
          const t = a.totals;
          const avail = pctText(t.available_s, t.seconds);
          const reg = a.registration || 'No registration';
          return (
            <li key={a.asset_id} className={`grid grid-cols-1 ${label} gap-x-3 gap-y-1 items-center`} data-asset={a.asset_id}>
              <div className={`min-w-0 ${phone ? 'flex items-baseline justify-between gap-2' : ''}`}>
                <span className="block min-w-0 truncate leading-tight">
                  <span className="font-mono font-semibold text-sm text-slate-900 whitespace-nowrap">{reg}</span>
                  {a.fleet_no && <span className="ml-1.5 text-xs text-slate-500">{a.fleet_no}</span>}
                </span>
                <span className="block flex-shrink-0 text-[11px] leading-tight tabular-nums text-slate-600" title="Available share of the window">
                  {avail} available
                </span>
              </div>
              <div className="relative h-7 rounded-md bg-slate-100 ring-1 ring-inset ring-slate-200 overflow-hidden" role="group" aria-label={`${reg}: ${avail} available`}>
                {ticks.map((tk) => (
                  <span
                    key={`t${tk.left}`}
                    className={`absolute inset-y-0 ${tk.major ? 'w-px bg-slate-300' : 'w-px bg-slate-200'}`}
                    style={{ left: `${tk.left}%` }}
                    aria-hidden="true"
                  />
                ))}
                {bars.map((b) => {
                  const s = b.segment;
                  const e = s.event_id ? eventsById.get(s.event_id) : undefined;
                  const text = barLabel(s, e);
                  return (
                    <button
                      key={`${s.start}-${s.event_id}`}
                      type="button"
                      onClick={() => s.event_id && onOpenEvent(s.event_id)}
                      className={`absolute inset-y-1 rounded ${CATEGORY_META[s.category].bar} min-w-[3px] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-500`}
                      style={{ left: `${b.left}%`, width: `${b.width}%`, ...(s.projected ? PROJECTED_STYLE : {}) }}
                      aria-label={text}
                      title={text}
                      data-category={s.category}
                      data-projected={s.projected ? '' : undefined}
                    />
                  );
                })}
                {nowAt !== null && <span className="absolute inset-y-0 w-0.5 bg-primary-600" style={{ left: `${nowAt}%` }} aria-hidden="true" />}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
